import { Box, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	getStripeSchoolSeats,
	getStripeSchoolTeachers,
} from '../../../actions/stripeSchoolTeachers.actions';
import {
	GET_STRIPE_SCHOOL_SEATS,
	GET_STRIPE_SCHOOL_TEACHERS,
} from '../../../actions/stripeSchoolTeachers.actionTypes';
import { palette } from '../../../assets/styles/themes/tokens';
import {
	selectStripeSchoolSeats,
	selectStripeSchoolSeatsLoading,
	selectStripeSchoolTeachers,
	selectStripeSchoolTeachersLoading,
} from '../../../reducers/stripeSchoolTeachers.selectors';
import { selectSelectedSchool, selectSelectedSchoolId } from '../../../reducers/user.selectors';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';
import useAction from '../../../utils/useAction';
import SeatOverviewHeader from '../SeatOverviewHeader/SeatOverviewHeader';
import StripeTeacherList from '../StripeTeacherList/StripeTeacherList';

const ROWS_PER_PAGE = 50;

const StripeTeachersPage: React.FC = () => {
	const [page, setPage] = React.useState(0);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');

	const selectedSchoolId = useSelector(selectSelectedSchoolId);
	const selectedSchool = useSelector(selectSelectedSchool);
	const teachersResponse = useSelector(selectStripeSchoolTeachers);
	const teachersLoading = useSelector(selectStripeSchoolTeachersLoading);
	const seats = useSelector(selectStripeSchoolSeats);
	const seatsLoading = useSelector(selectStripeSchoolSeatsLoading);

	const getTeachers = useAction(getStripeSchoolTeachers);
	const getSeats = useAction(getStripeSchoolSeats);
	const { openSnackbar } = useContext(SnackbarContext);
	const { t } = useTranslation();

	// Debounce the search query so we don't hit the API on every keystroke.
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackbar + t are stable context/hook results
	useEffect(() => {
		if (!selectedSchoolId) return;

		getSeats(selectedSchoolId)?.then((action) => {
			if (action && action.type !== GET_STRIPE_SCHOOL_SEATS.SUCCESS) {
				openSnackbar({
					type: 'error',
					message: t('Error while fetching school seats'),
				});
			}
		});
	}, [selectedSchoolId]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackbar + t are stable context/hook results
	useEffect(() => {
		if (!selectedSchoolId) return;
		// Skip the fetch while debounce is still catching up — otherwise a
		// keystroke that resets `page` would fire a stale-query fetch before the
		// 500 ms window elapses. Once `debouncedSearchQuery` catches up the
		// effect re-runs and fetches with the intended term.
		if (searchQuery.trim() !== debouncedSearchQuery.trim()) return;

		getTeachers(selectedSchoolId, page + 1, ROWS_PER_PAGE, debouncedSearchQuery.trim())?.then(
			(action) => {
				if (action && action.type !== GET_STRIPE_SCHOOL_TEACHERS.SUCCESS) {
					openSnackbar({
						type: 'error',
						message: t('Error while fetching teachers'),
					});
				}
			}
		);
	}, [selectedSchoolId, page, searchQuery, debouncedSearchQuery]);

	const handlePageChange = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setSearchQuery(event.target.value);
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: 'rgb(249, 250, 251)',
				paddingTop: '24px',
				paddingBottom: '24px',
			}}
		>
			<Box className="container">
				<Typography
					variant="overline"
					sx={{
						color: palette.primary.main,
						fontWeight: 700,
						letterSpacing: '0.08em',
					}}
				>
					{t('School admin')}
				</Typography>
				<Typography variant="h4" sx={{ color: palette.text.primary, fontWeight: 700, marginTop: 1 }}>
					{selectedSchool?.name ?? ''}
				</Typography>

				<Tabs value={0} sx={{ marginTop: 3, marginBottom: 3 }}>
					<Tab label={t('Teachers & Invites')} />
				</Tabs>

				<SeatOverviewHeader seats={seats} loading={seatsLoading} />

				<Box display="flex" alignItems="center" marginBottom={2}>
					<TextField
						size="small"
						placeholder={t('Search teachers by name or email')}
						variant="outlined"
						value={searchQuery}
						onChange={handleSearchChange}
						inputProps={{ 'aria-label': t('Search teachers by name or email') }}
						sx={{ flex: 1, maxWidth: 480 }}
					/>
				</Box>

				<StripeTeacherList
					teachers={teachersResponse?.items ?? []}
					total={teachersResponse?.total ?? 0}
					page={page}
					rowsPerPage={ROWS_PER_PAGE}
					loading={teachersLoading}
					onPageChange={handlePageChange}
				/>
			</Box>
		</Box>
	);
};

export default StripeTeachersPage;
